import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import type { UserProfile, SubscriptionInfo, AccessState } from '@/types';

/**
 * User Profiles and Subscription Service
 * Manages user profile data, subscription information, and access control
 */

const USERS_COLLECTION = 'users';
const SETTINGS_COLLECTION = 'settings';

/**
 * Default trial duration (configurable from Firestore settings)
 */
const DEFAULT_TRIAL_DAYS = 7;

/**
 * Get system settings from Firestore
 */
export const getSystemSettings = async (): Promise<{ trialDurationDays: number }> => {
  try {
    const settingsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'app'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      return {
        trialDurationDays: data.trialDurationDays || DEFAULT_TRIAL_DAYS,
      };
    }
    return { trialDurationDays: DEFAULT_TRIAL_DAYS };
  } catch (error) {
    console.error('Error fetching system settings:', error);
    return { trialDurationDays: DEFAULT_TRIAL_DAYS };
  }
};

/**
 * Create initial user profile with trial subscription
 */
export const createUserProfile = async (
  uid: string, 
  email: string, 
  displayName?: string
): Promise<UserProfile> => {
  try {
    // Get configurable trial duration
    const settings = await getSystemSettings();
    const trialDurationDays = settings.trialDurationDays;
    
    const now = new Date();
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + trialDurationDays);

    const subscription: SubscriptionInfo = {
      id: `trial_${uid}_${Date.now()}`,
      userId: uid,
      status: 'active',
      startDate: now,
      endDate: endDate,
      autoRenew: false,
      createdAt: now,
      updatedAt: now,
    };

    const userProfile: UserProfile = {
      id: uid,
      uid: uid,
      email: email,
      displayName: displayName || '',
      subscription: subscription,
      createdAt: now,
      updatedAt: now,
    };

    // Save to Firestore
    await setDoc(doc(db, USERS_COLLECTION, uid), {
      ...userProfile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      subscription: {
        ...subscription,
        startDate: Timestamp.fromDate(subscription.startDate),
        endDate: Timestamp.fromDate(subscription.endDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
    });

    return userProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Get user profile by UID
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    
    // Convert Firestore timestamps to Date objects
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      subscription: {
        ...data.subscription,
        startDate: data.subscription.startDate?.toDate() || new Date(),
        endDate: data.subscription.endDate?.toDate() || new Date(),
        createdAt: data.subscription.createdAt?.toDate() || new Date(),
        updatedAt: data.subscription.updatedAt?.toDate() || new Date(),
      },
    } as UserProfile;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  uid: string, 
  updates: Partial<UserProfile>
): Promise<void> => {
  try {
    const docRef = doc(db, USERS_COLLECTION, uid);
    
    // Note: Firestore's updateDoc typing is very complex when mixing user-defined types 
    // with FieldValue (serverTimestamp) and nested object updates. The type assertion 
    // below is safe because we control the exact structure being passed to Firestore.
    // This pattern is commonly used in production Firebase applications.
    
    if (updates.subscription) {
      // Handle subscription updates with date field conversion
      const subscriptionUpdate = { ...updates.subscription };
      if (updates.subscription.startDate) {
        (subscriptionUpdate as Record<string, unknown>).startDate = Timestamp.fromDate(updates.subscription.startDate);
      }
      if (updates.subscription.endDate) {
        (subscriptionUpdate as Record<string, unknown>).endDate = Timestamp.fromDate(updates.subscription.endDate);
      }
      (subscriptionUpdate as Record<string, unknown>).updatedAt = serverTimestamp();
      
      await updateDoc(docRef, {
        ...updates,
        subscription: subscriptionUpdate,
        updatedAt: serverTimestamp(),
      } as never);
    } else {
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      } as never);
    }
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};/**
 * Calculate access state based on subscription information
 */
export const calculateAccessState = (subscription: SubscriptionInfo): AccessState => {
  const now = new Date();
  const endDate = new Date(subscription.endDate);
  const timeDiff = endDate.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  
  const isExpired = now > endDate;
  const hasAccess = !isExpired && subscription.status === 'active';
  
  // Grace period logic (can be configured later for payment gateway)
  const gracePeriodDays = 0; // Currently no grace period
  const isGracePeriod = false;

  return {
    hasAccess,
    daysRemaining,
    isExpired,
    isGracePeriod,
    gracePeriodDays,
    expirationDate: endDate,
    canExtend: true, // Always allow extension for now
    subscriptionStatus: subscription.status,
  };
};

/**
 * Check current access state for a user
 */
export const checkUserAccess = async (uid: string): Promise<AccessState> => {
  try {
    const userProfile = await getUserProfile(uid);
    
    if (!userProfile) {
      throw new Error('User profile not found');
    }

    return calculateAccessState(userProfile.subscription);
  } catch (error) {
    console.error('Error checking user access:', error);
    throw error;
  }
};

/**
 * Extend user access by adding days to subscription
 */
export const extendUserAccess = async (
  uid: string, 
  extensionDays: number, 
  reason?: string
): Promise<void> => {
  try {
    const userProfile = await getUserProfile(uid);
    
    if (!userProfile) {
      throw new Error('User profile not found');
    }

    // Calculate new end date
    const currentEndDate = new Date(userProfile.subscription.endDate);
    const newEndDate = new Date(currentEndDate);
    newEndDate.setDate(newEndDate.getDate() + extensionDays);

    // Update subscription
    const updatedSubscription: SubscriptionInfo = {
      ...userProfile.subscription,
      endDate: newEndDate,
      status: 'active', // Reactivate if expired
      updatedAt: new Date(),
    };

    await updateUserProfile(uid, {
      subscription: updatedSubscription,
    });

    // Log extension (you can add to a separate analytics collection if needed)
    console.log(`Extended access for user ${uid} by ${extensionDays} days. Reason: ${reason || 'No reason provided'}`);
  } catch (error) {
    console.error('Error extending user access:', error);
    throw error;
  }
};

/**
 * Get user access summary for UI display
 */
export const getUserAccessSummary = async (uid: string): Promise<{
  daysRemaining: number;
  accessExpiry: string;
  status: string;
  canExtend: boolean;
}> => {
  try {
    const accessState = await checkUserAccess(uid);
    
    return {
      daysRemaining: accessState.daysRemaining,
      accessExpiry: accessState.expirationDate.toLocaleDateString(),
      status: accessState.isExpired ? 'Expired' : 'Active',
      canExtend: accessState.canExtend,
    };
  } catch (error) {
    console.error('Error getting user access summary:', error);
    throw error;
  }
};